'use client'

import { fetchBackendEvents, fetchBackendMarkets } from '@/lib/api/backend'
import type { BackendEventsParams } from '@/lib/api/backend-types'
import type { SortKey } from '@/lib/api/params'
import { MergedEventStream, MergedMarketStream } from '@/lib/merge-stream'
import type { FilterConfig, SortConfig } from '@/lib/merge-stream/types'
import {
	DEFAULT_FILTERS,
	DEFAULT_SORT,
	hasActiveFilters,
	hasLegacyPlatforms,
} from '@/lib/merge-stream/types'
import type { EventListItem, MarketListItem, Platform } from '@/types/market'
import { create } from 'zustand'

const BATCH_SIZE = 20

const BACKEND_PLATFORMS: Platform[] = ['polymarket', 'kalshi']

// Resolve the `source` param for the backend API by only considering backend-supported platforms
function resolveBackendSource(platforms: Platform[]): 'polymarket' | 'kalshi' | 'all' {
	const backendOnly = platforms.filter(p => BACKEND_PLATFORMS.includes(p))
	if (backendOnly.length === 1) return backendOnly[0] as 'polymarket' | 'kalshi'
	return 'all'
}

// Helper to check if an event has ended (endDate is unix seconds)
function isEventEnded(endDate: number | undefined): boolean {
	if (!endDate) return false
	return endDate * 1000 < Date.now()
}

// Helper to check if a market has ended (closeTime is unix ms) - unused, kept for filterNewMarkets
// function isMarketEnded(closeTime: number): boolean {
// 	return closeTime < Date.now()
// }

// Helper to filter and deduplicate events
function filterNewEvents(
	newEvents: EventListItem[],
	existingEvents: EventListItem[],
	filters: FilterConfig
): EventListItem[] {
	const existingIds = new Set(existingEvents.map(e => `${e.platform}:${e.id}`))
	const activeOnly = filters.activeOnly !== false

	return newEvents.filter(e => {
		if (existingIds.has(`${e.platform}:${e.id}`)) return false
		if (activeOnly && (e.isInactive || isEventEnded(e.endDate))) return false
		return true
	})
}

// Helper to filter and deduplicate markets (unused - kept for future use)
// function filterNewMarkets(
// 	newMarkets: MarketListItem[],
// 	existingMarkets: MarketListItem[],
// 	filters: FilterConfig
// ): MarketListItem[] {
// 	const existingIds = new Set(existingMarkets.map(m => `${m.platform}:${m.id}`))
// 	const activeOnly = filters.activeOnly !== false
//
// 	return newMarkets.filter(m => {
// 		if (existingIds.has(`${m.platform}:${m.id}`)) return false
// 		if (activeOnly && (m.status !== 'active' || isMarketEnded(m.closeTime))) return false
// 		return true
// 	})
// }

type MarketsState = {
	// Events data
	events: EventListItem[]
	eventsLoading: boolean
	eventsHasMore: boolean
	eventsError: Error | null
	eventsSort: SortConfig
	eventsFilters: FilterConfig

	// Markets data
	markets: MarketListItem[]
	marketsLoading: boolean
	marketsHasMore: boolean
	marketsError: Error | null
	marketsSort: SortConfig
	marketsFilters: FilterConfig

	// Streams (kept in memory, internal use)
	eventStream: MergedEventStream | null
	marketStream: MergedMarketStream | null

	// Backend mode state for events
	useBackendMode: boolean
	backendOffset: number

	// Backend mode state for markets
	useMarketsBackendMode: boolean
	backendMarketsOffset: number

	// Actions
	loadMoreEvents: () => Promise<void>
	loadMoreMarkets: () => Promise<void>
	setEventsSort: (sort: SortConfig) => void
	setMarketsSort: (sort: SortConfig) => void
	setEventsFilters: (filters: FilterConfig) => void
	setMarketsFilters: (filters: FilterConfig) => void
	resetEvents: () => void
	resetMarkets: () => void
	prefetch: () => Promise<void>
}

export const useMarketsStore = create<MarketsState>((set, get) => ({
	// Initial state - events
	events: [],
	eventsLoading: false,
	eventsHasMore: true,
	eventsError: null,
	eventsSort: DEFAULT_SORT,
	eventsFilters: DEFAULT_FILTERS,

	// Initial state - markets
	markets: [],
	marketsLoading: false,
	marketsHasMore: true,
	marketsError: null,
	marketsSort: DEFAULT_SORT,
	marketsFilters: DEFAULT_FILTERS,

	// Streams
	eventStream: null,
	marketStream: null,

	// Backend mode for events
	useBackendMode: false,
	backendOffset: 0,

	// Backend mode for markets
	useMarketsBackendMode: false,
	backendMarketsOffset: 0,

	loadMoreEvents: async () => {
		const state = get()
		if (state.eventsLoading) {
			return
		}

		set({ eventsLoading: true, eventsError: null })

		try {
			if (state.useBackendMode) {
				// Fetch from backend
				const filters = state.eventsFilters
				const platforms = filters.platforms || ['kalshi', 'polymarket']

				// Map sort key to backend format
				const sortMap: Record<SortKey, BackendEventsParams['sort']> = {
					volume: 'volume',
					volume24h: 'volume',
					liquidity: 'liquidity',
					openInterest: 'volume',
					startDate: 'end_date',
				}

				// Backend expects capitalized categories (Politics, Crypto, etc)
				const capitalizeCategory = (c: string) => c.charAt(0).toUpperCase() + c.slice(1)

				const params: BackendEventsParams = {
					q: filters.search,
					category: filters.categories?.map(capitalizeCategory).join(','),
					source: resolveBackendSource(platforms),
					status: filters.activeOnly === false ? 'all' : 'active',
					sort: sortMap[state.eventsSort.key],
					order: 'desc',
					limit: BATCH_SIZE,
					offset: state.backendOffset,
					expiring_soon: filters.expiringSoon || undefined,
					closing_time: filters.closingTime,
					min_probability: filters.minProbability,
					min_volume: filters.minVolume,
					max_volume: filters.maxVolume,
					min_liquidity: filters.minLiquidity,
					max_liquidity: filters.maxLiquidity,
				}

				const result = await fetchBackendEvents(params)

				set(s => ({
					events: [...s.events, ...filterNewEvents(result.data, s.events, s.eventsFilters)],
					eventsHasMore: result.meta.hasMore,
					eventsLoading: false,
					backendOffset: s.backendOffset + result.data.length,
				}))
			} else {
				// Use MergedStream (existing logic)
				let stream = state.eventStream
				if (!stream) {
					stream = new MergedEventStream(state.eventsSort, state.eventsFilters)
					set({ eventStream: stream })
				}

				const newEvents = await stream.getNext(BATCH_SIZE)

				set(s => ({
					events: [...s.events, ...filterNewEvents(newEvents, s.events, s.eventsFilters)],
					eventsHasMore: stream?.hasMore() ?? false,
					eventsLoading: false,
				}))
			}
		} catch (err) {
			console.error('Error loading events:', err)
			set({
				eventsError: err instanceof Error ? err : new Error('Failed to load events'),
				eventsLoading: false,
			})
		}
	},

	loadMoreMarkets: async () => {
		const state = get()
		if (state.marketsLoading) {
			return
		}

		set({ marketsLoading: true, marketsError: null })

		try {
			if (state.useMarketsBackendMode) {
				// Fetch from backend
				const filters = state.marketsFilters
				const platforms = filters.platforms || ['kalshi', 'polymarket']

				// Map sort key to backend format
				const sortMap: Record<SortKey, BackendEventsParams['sort']> = {
					volume: 'volume',
					volume24h: 'volume',
					liquidity: 'liquidity',
					openInterest: 'volume',
					startDate: 'end_date',
				}

				// Backend expects capitalized categories (Politics, Crypto, etc)
				const capitalizeCategory = (c: string) => c.charAt(0).toUpperCase() + c.slice(1)

				const params: BackendEventsParams = {
					q: filters.search,
					category: filters.categories?.map(capitalizeCategory).join(','),
					source: resolveBackendSource(platforms),
					status: filters.activeOnly === false ? 'all' : 'active',
					sort: sortMap[state.marketsSort.key],
					order: 'desc',
					limit: BATCH_SIZE,
					offset: state.backendMarketsOffset,
					expiring_soon: filters.expiringSoon || undefined,
					closing_time: filters.closingTime,
					min_probability: filters.minProbability,
					min_volume: filters.minVolume,
					max_volume: filters.maxVolume,
					min_liquidity: filters.minLiquidity,
					max_liquidity: filters.maxLiquidity,
				}

				const result = await fetchBackendMarkets(params)

				set(s => {
					const existingIds = new Set(s.markets.map(m => `${m.platform}:${m.id}`))
					const uniqueNewMarkets = result.data.filter(
						m => !existingIds.has(`${m.platform}:${m.id}`)
					)
					return {
						markets: [...s.markets, ...uniqueNewMarkets],
						marketsHasMore: result.meta.hasMore,
						marketsLoading: false,
						backendMarketsOffset: s.backendMarketsOffset + result.data.length,
					}
				})
			} else {
				// Use MergedStream (existing logic)
				let stream = state.marketStream
				if (!stream) {
					stream = new MergedMarketStream(state.marketsSort, state.marketsFilters)
					set({ marketStream: stream })
				}

				const newMarkets = await stream.getNext(BATCH_SIZE)

				set(s => {
					// Deduplicate in case of race conditions
					const existingIds = new Set(s.markets.map(m => `${m.platform}:${m.id}`))
					const uniqueNewMarkets = newMarkets.filter(m => !existingIds.has(`${m.platform}:${m.id}`))
					return {
						markets: [...s.markets, ...uniqueNewMarkets],
						marketsHasMore: stream?.hasMore() ?? false,
						marketsLoading: false,
					}
				})
			}
		} catch (err) {
			console.error('Error loading markets:', err)
			set({
				marketsError: err instanceof Error ? err : new Error('Failed to load markets'),
				marketsLoading: false,
			})
		}
	},

	setEventsSort: (sort: SortConfig) => {
		const state = get()
		if (sort.key === state.eventsSort.key && sort.direction === state.eventsSort.direction) {
			return
		}

		if (state.useBackendMode) {
			// Keep backend mode, just reset
			set({
				eventsSort: sort,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				backendOffset: 0,
			})
		} else {
			// Create new stream
			const stream = new MergedEventStream(sort, state.eventsFilters)
			set({
				eventsSort: sort,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: stream,
			})
		}

		get().loadMoreEvents()
	},

	setMarketsSort: (sort: SortConfig) => {
		const state = get()
		if (sort.key === state.marketsSort.key && sort.direction === state.marketsSort.direction) {
			return
		}

		if (state.useMarketsBackendMode) {
			// Keep backend mode, just reset
			set({
				marketsSort: sort,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				backendMarketsOffset: 0,
			})
		} else {
			// Create new stream
			const stream = new MergedMarketStream(sort, state.marketsFilters)
			set({
				marketsSort: sort,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: stream,
			})
		}

		// Immediately fetch first batch
		get().loadMoreMarkets()
	},

	setEventsFilters: (filters: FilterConfig) => {
		const state = get()
		const platforms = filters.platforms || ['kalshi', 'polymarket', 'opinionlabs', 'predictfun']
		const shouldUseBackend = hasActiveFilters(filters) || !hasLegacyPlatforms(platforms)

		if (shouldUseBackend) {
			// Use backend API
			set({
				eventsFilters: filters,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: null,
				useBackendMode: true,
				backendOffset: 0,
			})
		} else {
			// Use MergedStream
			const stream = new MergedEventStream(state.eventsSort, filters)
			set({
				eventsFilters: filters,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: stream,
				useBackendMode: false,
				backendOffset: 0,
			})
		}

		get().loadMoreEvents()
	},

	setMarketsFilters: (filters: FilterConfig) => {
		const state = get()
		const platforms = filters.platforms || ['kalshi', 'polymarket', 'opinionlabs', 'predictfun']
		const shouldUseBackend = hasActiveFilters(filters) || !hasLegacyPlatforms(platforms)

		if (shouldUseBackend) {
			// Use backend API
			set({
				marketsFilters: filters,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: null,
				useMarketsBackendMode: true,
				backendMarketsOffset: 0,
			})
		} else {
			// Use MergedStream
			const stream = new MergedMarketStream(state.marketsSort, filters)
			set({
				marketsFilters: filters,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: stream,
				useMarketsBackendMode: false,
				backendMarketsOffset: 0,
			})
		}

		get().loadMoreMarkets()
	},

	resetEvents: () => {
		const state = get()

		if (state.useBackendMode) {
			set({
				events: [],
				eventsHasMore: true,
				eventsError: null,
				backendOffset: 0,
			})
		} else {
			const stream = new MergedEventStream(state.eventsSort, state.eventsFilters)
			set({
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: stream,
			})
		}

		get().loadMoreEvents()
	},

	resetMarkets: () => {
		const state = get()

		if (state.useMarketsBackendMode) {
			set({
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				backendMarketsOffset: 0,
			})
		} else {
			const stream = new MergedMarketStream(state.marketsSort, state.marketsFilters)
			set({
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: stream,
			})
		}
		get().loadMoreMarkets()
	},

	// Prefetch both events and markets on app start
	prefetch: async () => {
		const state = get()

		// Only prefetch if we haven't loaded anything yet
		if (state.events.length === 0 && !state.eventsLoading) {
			get().loadMoreEvents()
		}
		if (state.markets.length === 0 && !state.marketsLoading) {
			get().loadMoreMarkets()
		}
	},
}))
